import type { ReleaseStatus, ReleaseWithMetrics } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Rocket } from 'lucide-react';
import { useReleasesAdminContent } from '../../content/sections/releases-admin.js';

/* Tabela de releases do app sobre o primitive `Table` de @planuze/ui (ADR-0118/0659/0702/0809). A
   busca/filtros são server-side na page (a lista é paginada por cursor); a tabela diferencia
   "sem itens" de "sem resultados" via `hasFilters`. As ações (promover/tirar do ar/apagar/editar
   notas) chegam por callback — a lógica de mutation e as confirmações vivem na page. */

function statusTone(status: ReleaseStatus): 'success' | 'neutral' | 'destructive' {
  if (status === 'promoted') return 'success';
  if (status === 'yanked') return 'destructive';
  return 'neutral';
}

/* `Intl` e não template: 12345 downloads lidos como "12.345" pouparam a conferência de dígito. */
const numberFormat = new Intl.NumberFormat('pt-BR');

interface Props {
  readonly releases: readonly ReleaseWithMetrics[];
  readonly canManage: boolean;
  /** Alguma mutation (promote/takedown/delete/lote) em flight — desabilita as ações. */
  readonly busy: boolean;
  /** Há busca ativa — diferencia "sem resultados" de "sem itens" (ADR-0702/§6). */
  readonly hasFilters: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly onClearFilters: () => void;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
  readonly onPromote: (release: ReleaseWithMetrics, mandatory: boolean) => void;
  readonly onTakedown: (release: ReleaseWithMetrics) => void;
  readonly onSetMandatory: (
    channel: ReleaseWithMetrics['channel'],
    version: string,
    mandatory: boolean,
  ) => void;
  readonly onDelete: (release: ReleaseWithMetrics) => void;
  readonly onPurge: (release: ReleaseWithMetrics) => void;
  readonly onEditNotes: (release: ReleaseWithMetrics) => void;
}

export function ReleasesTable({
  releases,
  canManage,
  busy,
  hasFilters,
  selectedIds,
  onClearFilters,
  onToggle,
  onToggleAll,
  onPromote,
  onTakedown,
  onSetMandatory,
  onDelete,
  onPurge,
  onEditNotes,
}: Props) {
  const copy = useReleasesAdminContent().table;
  if (releases.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<Rocket className="size-6" />}
        title={copy.emptyFilteredTitle}
        description={copy.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<Rocket className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
      />
    );
  }
  const allSelected = releases.length > 0 && releases.every((r) => selectedIds.has(r.id));
  const purgeReleaseByVersion = new Map<string, string>();
  const promotedVersions = new Set<string>();
  for (const release of releases) {
    const versionKey = `${release.channel}/${release.version}`;
    if (release.status === 'promoted') promotedVersions.add(versionKey);
    if (release.storageState !== 'deleted' && !purgeReleaseByVersion.has(versionKey)) {
      purgeReleaseByVersion.set(versionKey, release.id);
    }
  }
  for (const versionKey of promotedVersions) purgeReleaseByVersion.delete(versionKey);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canManage && (
            <TableHead className="w-10">
              {/* Seleciona só o que está NA TELA — a lista é paginada e "todas" incluiria versões
                  que o staff nunca viu, o que num lote destrutivo seria inaceitável. */}
              <Checkbox
                checked={allSelected}
                onCheckedChange={(c) => onToggleAll(c === true)}
                aria-label={copy.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.columnVersion}</TableHead>
          <TableHead>{copy.columnChannel}</TableHead>
          <TableHead>{copy.columnPlatform}</TableHead>
          <TableHead>{copy.columnStatus}</TableHead>
          <TableHead>{copy.columnMandatory}</TableHead>
          <TableHead>{copy.columnDownloads}</TableHead>
          {canManage && <TableHead>{copy.columnActions}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {releases.map((release) => (
          <TableRow key={release.id}>
            {canManage && (
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(release.id)}
                  onCheckedChange={() => onToggle(release.id)}
                  aria-label={copy.selectRowAriaLabel(
                    release.version,
                    `${release.platform}/${release.arch}`,
                  )}
                />
              </TableCell>
            )}
            <TableCell className="font-medium text-foreground">{release.version}</TableCell>
            <TableCell className="text-muted-foreground">{release.channel}</TableCell>
            <TableCell className="text-muted-foreground">
              {release.platform}/{release.arch}
              {/* O sufixo só aparece no instalador: `atualização` é o caso comum e rotular
                  todas as linhas cansaria a leitura da tabela. */}
              {release.kind === 'installer' ? (
                <span className="ml-2 text-xs text-muted-foreground">{copy.installerSuffix}</span>
              ) : null}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge variant={statusTone(release.status)}>{release.status}</StatusBadge>
                {release.storageState === 'deleting' && (
                  <StatusBadge variant="warning">{copy.storageDeletingBadge}</StatusBadge>
                )}
                {release.storageState === 'deleted' && (
                  <StatusBadge variant="destructive">{copy.storageDeletedBadge}</StatusBadge>
                )}
              </div>
            </TableCell>
            <TableCell>
              {release.mandatory ? (
                <StatusBadge variant="warning">{copy.mandatoryBadge}</StatusBadge>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground tabular-nums">
              {release.downloadCount === 0 ? (
                <span>—</span>
              ) : (
                <span title={copy.lastDownloadTitle(release.lastDownloadAt ?? '')}>
                  {numberFormat.format(release.downloadCount)}
                </span>
              )}
            </TableCell>
            {canManage && (
              <TableCell>
                <div className="flex flex-wrap gap-2">
                  {release.status !== 'promoted' && release.storageState === 'present' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onPromote(release, false)}
                      >
                        {copy.promote}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onPromote(release, true)}
                      >
                        {copy.promoteMandatory}
                      </Button>
                    </>
                  )}
                  {release.status === 'promoted' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          onSetMandatory(release.channel, release.version, !release.mandatory)
                        }
                      >
                        {release.mandatory ? copy.markOptional : copy.markMandatory}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onTakedown(release)}
                      >
                        {copy.takedown}
                      </Button>
                    </>
                  )}
                  {release.status === 'promoted' && (
                    <Button variant="ghost" size="sm" onClick={() => onEditNotes(release)}>
                      {copy.editNotes}
                    </Button>
                  )}
                  {/* Só fora do ar: apagar uma promovida é recusado pelo service, e oferecer o
                      botão para depois negá-lo ensinaria o staff a ignorar a recusa. */}
                  {release.status !== 'promoted' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        disabled={busy}
                        onClick={() => onDelete(release)}
                      >
                        {copy.delete}
                      </Button>
                      {purgeReleaseByVersion.get(`${release.channel}/${release.version}`) ===
                        release.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          disabled={busy}
                          onClick={() => onPurge(release)}
                        >
                          {release.storageState === 'deleting' ? copy.continuePurge : copy.purge}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
